import {ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {BehaviorSubject, catchError, EMPTY, Subject, takeUntil} from "rxjs";
import {AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators} from "@angular/forms";
import {map} from "rxjs/operators";
import { HttpErrorResponse } from "@angular/common/http";
import {SharedService} from "@shared/shared.service";
import {BillingService} from "@shared/services/billing.service";
import {ToastrService} from "ngx-toastr";
import {TranslateService} from "@ngx-translate/core";
import {ConfigService} from "@config/config.service";
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {BillingRefundDraftModel} from "@services/back-office/model/billingRefundDraftModel";
import {BillingRefundRequest} from "@services/back-office/model/billingRefundRequest";

/** The server trims before it measures, so the form has to measure the trimmed value too. */
function trimmedLength(min: number, max: number) {
    return (control: AbstractControl) => {
        const length = (control.value ? String(control.value) : '').trim().length;
        return length >= min && length <= max ? null : {trimmedLength: true};
    };
}

/**
 * «Повернути кошти»: a payment order to the client bank from the Dah account the contract is paid into. The draft
 * arrives prefilled with the contract's overpayment and the payer of its last incoming payment.
 */
@Component({
    selector: 'app-refund-dialog',
    templateUrl: './refund-dialog.component.html',
    styleUrls: ['./refund-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class RefundDialogComponent implements OnInit, OnDestroy {
    private destroyed$ = new Subject<void>();
    loading$ = new BehaviorSubject(false);
    draft: BillingRefundDraftModel;
    refundForm: UntypedFormGroup;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: {
            contractId: string,
            contractName?: string
        },
        public dialogRef: MatDialogRef<RefundDialogComponent>,
        private formBuilder: UntypedFormBuilder,
        private config: ConfigService,
        private billingService: BillingService,
        private toastrService: ToastrService,
        private translateService: TranslateService,
    ) {
    }

    ngOnInit(): void {
        this.initForm();
        this.getDraft();
    }

    ngOnDestroy() {
        this.destroyed$.next();
        this.destroyed$.complete();
        this.loading$.complete();
    }

    initForm() {
        this.refundForm = this.formBuilder.group({
            amount: ['', [Validators.required, Validators.min(0.01), Validators.pattern(this.config.regex.moneyTwoDigits)]],
            comment: ['', [Validators.required, trimmedLength(5, 420)]],
            recipientName: ['', [Validators.required, trimmedLength(1, 255)]],
            recipientCode: ['', [Validators.required, Validators.pattern(/^(\d{8}|\d{10})$/)]],
            recipientAccount: ['', [Validators.required, Validators.pattern(/^UA\d{27}$/)]],
        });
    }

    getDraft() {
        this.loading$.next(true);
        this.billingService.getRefundDraft(this.data.contractId).pipe(
            takeUntil(this.destroyed$),
            map((res: BillingRefundDraftModel) => {
                this.draft = res;
                this.refundForm.patchValue({
                    ...res.amount && {amount: res.amount},
                    ...res.comment && {comment: res.comment},
                    ...res.recipient?.name && {recipientName: res.recipient.name},
                    ...res.recipient?.id && {recipientCode: res.recipient.id},
                    ...res.recipient?.account && {recipientAccount: res.recipient.account},
                });
                this.loading$.next(false);
            }),
            catchError((error: HttpErrorResponse) => {
                SharedService.showErrorWithTitle(
                    this.translateService,
                    this.toastrService,
                    `responseErrors.${error.error.message}`
                );
                this.closeDialog(false);
                return EMPTY;
            })
        ).subscribe();
    }

    submitForm() {
        if (this.refundForm.invalid) {
            this.refundForm.markAllAsTouched();
            return;
        }

        const formData = this.refundForm.getRawValue();
        const request: BillingRefundRequest = {
            contractId: this.data.contractId,
            amount: Number(formData.amount),
            comment: formData.comment.trim(),
            recipient: {
                id: formData.recipientCode.trim(),
                name: formData.recipientName.trim(),
                account: formData.recipientAccount.trim().toUpperCase()
            }
        };

        this.loading$.next(true);
        this.billingService.createRefund(request).pipe(
            takeUntil(this.destroyed$),
            map(() => {
                this.toastrService.success('Платіжне доручення відправлено в банк');
                this.loading$.next(false);
                this.closeDialog(true);
            }),
            catchError((error: HttpErrorResponse) => {
                SharedService.showErrorWithTitle(
                    this.translateService,
                    this.toastrService,
                    `responseErrors.${error.error.message}`
                );
                this.loading$.next(false);
                return EMPTY;
            })
        ).subscribe();
    }

    closeDialog(result: boolean, data?: any) {
        this.dialogRef.close({result, data});
    }
}
